import { ThunkAction } from 'redux-thunk';
import { RootState } from 'src/_common/models/redux/store';
import { openToastr } from 'src/_common/handlers/services';
import {
  UserChangedModeAction,
  UserChangedLengthGaugeAction,
  UserClickedModeExpansionPanelAction,
  UserClickedDeleteTextAction,
  UserClickedUndoDeleteTextAction,
  UserClickedCopyAllAction,
  UserClickedInsertToDocAction,
  CharLimitExceedAction,
  UserClickedParaphraseSummaryAction,
  UserUploadedFile,
} from '../models/actions';
import {
  USER_CLICKED_MODE_EXPANSION_PANEL,
  USER_CHANGED_ACTIVE_TAB,
  USER_CLICKED_INSERT_TEXT,
  CHAR_LIMIT_EXCEEDED,
  USER_CLICKED_PARAPHRASE_SUMMARY,
  USER_UPLOADED_FILE,
} from '../models/actions/names';
import {
  userChangedModeAction,
  userChangedLengthGaugeAction,
  userClickedDeleteTextAction,
  userClickUndoDeleteTextAction,
  userClickedCopyAllAction,
} from './systemActions';
import { getSummarySentenceSequence, getOutputKey } from '../handlers/logic';
import { summarizeAction, summarizeActionForSelectedText, SummarizeActions } from './quillActions';

export const changeSummaryModeAction =
  (
    newMode: 'abs' | 'ext'
  ): ThunkAction<void, RootState, null, UserChangedModeAction | SummarizeActions> =>
  (dispatch, getState) => {
    const cs = getState().smzr;
    const absLengthGauge = cs.controls.absLengthGauge;
    dispatch(userChangedModeAction(newMode, absLengthGauge));
    dispatch(summarizeAction());
  };

export const changeSummaryModeActionForWriter =
  (
    newMode: 'abs' | 'ext',
    text: string
  ): ThunkAction<void, RootState, null, UserChangedModeAction | SummarizeActions> =>
  (dispatch, getState) => {
    const cs = getState().smzr;
    const absCurrentValue = cs.controls.absLengthGauge;
    dispatch(userChangedModeAction(newMode, absCurrentValue));
    dispatch(summarizeActionForSelectedText(text));
  };

export const changeLengthGaugeAction =
  (updatedValue: number): ThunkAction<void, RootState, null, UserChangedLengthGaugeAction> =>
  (dispatch, getState) => {
    let newValue = updatedValue;
    const cs = getState().smzr;
    const summaryMode = cs.controls.summaryMode as 'ext' | 'abs';
    let summarySentenceSequence: string[] = [];

    if (summaryMode === 'ext') {
      const extOutput = cs.output.ext;
      if (extOutput && Object.keys(extOutput).length !== 0) {
        summarySentenceSequence = getSummarySentenceSequence(extOutput, newValue);
      }
    } else if (summaryMode === 'abs') {
      newValue /= 10;
      const output = cs.output[getOutputKey(summaryMode, newValue)];

      if (output === undefined && cs.output.isAbsSummaryExist && cs.input.inputPara.length) {
        openToastr('Click ReSummarize to update', 3000);
      }
    }
    /* Sending absLengthGauge to handle thinkingTime of diff Abs length Outputs */
    const absCurrentValue = cs.controls.absLengthGauge;
    dispatch(
      userChangedLengthGaugeAction(newValue, summaryMode, summarySentenceSequence, absCurrentValue)
    );
  };

export const charLimitMsgAction = (value: boolean): CharLimitExceedAction => ({
  type: CHAR_LIMIT_EXCEEDED,
  payload: { charLimitExceded: value },
});

export const quillItAction =
  (
    triggeredBy = 'summarizeButton'
  ): ThunkAction<void, RootState, null, SummarizeActions | CharLimitExceedAction> =>
  (dispatch) => {
    dispatch(summarizeAction(triggeredBy));
    dispatch(charLimitMsgAction(false));
  };

export const userChangedActiveTabAction = (newTab: 'summarized' | 'original') => ({
  type: USER_CHANGED_ACTIVE_TAB,
  payload: { summaryActivetab: newTab },
});

export const userClickedModeExpansionPanelAction = (): UserClickedModeExpansionPanelAction => ({
  type: USER_CLICKED_MODE_EXPANSION_PANEL,
  payload: {},
});

export const userClickedInsertIntoDocAction = (
  insertedText: string
): UserClickedInsertToDocAction => ({
  type: USER_CLICKED_INSERT_TEXT,
  payload: {
    insertedText,
  },
});

export interface ParaphraseSummaryData {
  summary: string;
}

export const paraphraseSummaryAction = (
  value: ParaphraseSummaryData
): UserClickedParaphraseSummaryAction => ({
  type: USER_CLICKED_PARAPHRASE_SUMMARY,
  payload: {
    value,
  },
});

export const clearSummaryDataAction =
  (): ThunkAction<
    void,
    RootState,
    null,
    UserClickedDeleteTextAction | UserClickedUndoDeleteTextAction
  > =>
  (dispatch) => {
    dispatch(userClickedDeleteTextAction());
    openToastr('Text Deleted.', 3000, () => dispatch(userClickUndoDeleteTextAction()), 'Undo');
  };

export const copyAllText =
  (
    copiedText: string,
    triggeredBy: string
  ): ThunkAction<void, RootState, null, UserClickedCopyAllAction> =>
  (dispatch) => {
    dispatch(userClickedCopyAllAction(copiedText, triggeredBy));
  };

export const userUploadedFile = ({
  fileName,
  fileType,
}: {
  fileName: string;
  fileType: string;
}): UserUploadedFile => ({
  type: USER_UPLOADED_FILE,
  payload: { fileName, fileType },
});
